import { Modal } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useLocation } from 'react-router-dom';
import { useTrackedEffect } from 'ahooks';
import { LRUMap } from 'lib/lruMap';

export interface Tab {
  path: string;
  search: string;
  state?: any;
}

interface MultitabState {
  activeKey?: string;
  list: Tab[];
}

export const multitabReadyState = atom<boolean>(false);

export const useMultitabReady = () => useAtomValue(multitabReadyState);
export const useSetMultitabReady = () => useSetAtom(multitabReadyState);

export const multitabState = atom<MultitabState>({
  activeKey: undefined,
  list: [],
});
export const useMultitabState = () => useAtomValue(multitabState);
export const useSetMultitabState = () => useSetAtom(multitabState);

interface HistoryTab extends Tab {
  replace?: boolean;
}

export const multitabHistoryMap: Partial<Record<string, HistoryTab[]>> = {};
export const addMultitabHistory = (path: string, historyTab: HistoryTab) => {
  const prePath = historyTab.path;
  const history = multitabHistoryMap[prePath] || [];
  history.push(historyTab);
  deleteMultitabHistory(prePath);
  multitabHistoryMap[path] = history;
};
export const deleteMultitabHistory = (path: string) => delete multitabHistoryMap[path];

export const multitabDirtyMap: Partial<Record<string, boolean>> = {};
export const updateMultitabDirty = (path: string, dirty: boolean) =>
  (multitabDirtyMap[path] = dirty);
export const deleteMultitabDirty = (path: string) => delete multitabDirtyMap[path];

export const deleteMultitabState = (path: string) => {
  deleteMultitabHistory(path);
  deleteMultitabDirty(path);
};

export function useUpdateDirty(dirty: boolean) {
  const { pathname } = useLocation();

  useEffect(() => {
    updateMultitabDirty(pathname, dirty);
  }, [pathname, dirty]);
}

const findTargetIndex = (list: Tab[], path: string) =>
  list.findIndex(
    (tab) => tab.path === path || multitabHistoryMap[tab.path]?.some((_) => _.path === path),
  );

export function useTabOperations() {
  const { t } = useTranslate();

  const setMultitabState = useSetMultitabState();

  return useMemo(() => {
    const addTab = (tab: Tab, source?: string) => {
      setMultitabState((current) => {
        const { activeKey, list } = current;

        const targetIndex = findTargetIndex(list, tab.path);

        const target = list[targetIndex];

        if (target) {
          if (target.path !== tab.path || (tab.search && tab.search !== target.search)) {
            addMultitabHistory(tab.path, target);
            return {
              activeKey: tab.path,
              list: [...list.slice(0, targetIndex), tab, ...list.slice(targetIndex + 1)],
            };
          }
          if (activeKey !== tab.path) {
            return {
              activeKey: tab.path,
              list,
            };
          }
          return current;
        }

        const sourceIndex = source ? list.findIndex(({ path }) => path === source) : -1;

        const nextList =
          sourceIndex < 0
            ? [...list, tab]
            : [...list.slice(0, sourceIndex + 1), tab, ...list.slice(sourceIndex + 1)];

        return {
          activeKey: tab.path,
          list: nextList,
        };
      });
    };

    const updateTab = (path: string, nextTab: Tab, replace = true) => {
      setMultitabState((current) => {
        const { activeKey, list } = current;

        const currentTabIndex = findTargetIndex(list, path);
        const currentTab = list[currentTabIndex];

        const targetTabIndex = findTargetIndex(list, nextTab.path);

        if (!currentTab) {
          return current;
        }

        const nextActiveKey = activeKey === path ? nextTab.path : activeKey;
        const nextList = [
          ...list.slice(0, currentTabIndex),
          nextTab,
          ...list.slice(currentTabIndex + 1),
        ];

        addMultitabHistory(nextTab.path, { ...currentTab, replace });

        if (targetTabIndex === currentTabIndex) {
          return {
            activeKey: nextActiveKey,
            list: nextList,
          };
        }

        deleteMultitabState(path);

        return {
          activeKey: nextActiveKey,
          list: nextList.filter((_, index) => index !== targetTabIndex),
        };
      });
    };

    const closeTab = (closePath: string, force?: boolean) => {
      const { [closePath]: dirty = closePath.startsWith('/bootguide') } = multitabDirtyMap;

      const close = () => {
        setMultitabState((current) => {
          const { activeKey, list } = current;

          const index = findTargetIndex(list, closePath);

          if (index < 0) {
            return current;
          }

          return {
            activeKey:
              activeKey !== list[index].path
                ? activeKey
                : (list[index - 1] || list[index + 1])?.path,
            list: [...list.slice(0, index), ...list.slice(index + 1)],
          };
        });
        deleteMultitabState(closePath);
      };

      if (!dirty || force) {
        close();
      } else {
        Modal.confirm({
          title: t('knowledge.exitTip'),
          onOk: close,
        });
      }
    };

    const updateActiveKey = (path?: string) => {
      setMultitabState((current) => {
        if (path === current.activeKey) {
          return current;
        }
        return {
          ...current,
          activeKey: path,
        };
      });
    };

    const closeUnactiveTabs = () => {
      setMultitabState(({ activeKey, list }) => {
        const activePath = activeKey;
        const nextList = list.filter(({ path }) => path === activePath);

        list.forEach(({ path }) => {
          if (path !== activePath) {
            deleteMultitabState(path);
          }
        });

        return {
          activeKey,
          list: nextList,
        };
      });
    };

    return {
      addTab,
      updateTab,
      closeTab,
      updateActiveKey,
      t,
      closeUnactiveTabs,
    };
  }, [setMultitabState, t]);
}

export function useTabActiveBackend() {
  const lruMapRef = useRef(new LRUMap<string, number>(20));
  const [, forceUpdate] = useState(true);
  const { activeKey, list } = useMultitabState();

  useTrackedEffect(
    (_, preDeps = [], curDeps = []) => {
      const [preActiveKey] = preDeps as (string | undefined)[];
      const [curActiveKey] = curDeps as (string | undefined)[];
      const lruMap = lruMapRef.current;

      if (preActiveKey) {
        if (list.some(({ path }) => path === preActiveKey)) {
          lruMap.set(preActiveKey, Date.now());
        } else {
          lruMap.delete(preActiveKey);
        }
      }

      if (curActiveKey) {
        lruMap.set(curActiveKey, Date.now());
      }

      forceUpdate((_) => !_);
    },
    [activeKey],
  );

  const refsCurrent = { activeKey };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const intervalTime = 10 * 60 * 1000;

    const interval = () => {
      const now = Date.now();
      const lruMap = lruMapRef.current;
      const { activeKey } = refs.current;

      lruMap.keys().forEach((key) => {
        if (key !== activeKey && now - (lruMap.get(key) || 0) > intervalTime) {
          lruMap.delete(key);
        }
      });

      forceUpdate((_) => !_);
    };

    const timer = setInterval(interval, intervalTime);

    return () => clearInterval(timer);
  }, []);

  return useCallback(
    (path: string) => activeKey === path || Boolean(lruMapRef.current.get(path)),
    [activeKey],
  );
}
